import React, { useMemo, useState } from 'react'
import { Drawer } from 'antd'
import dayjs from 'dayjs'

import {
  HistoryEvent,
  useBenefitActivity,
} from '../../api/reconciliation-v2-api-client'
import { formatAmount } from './activity-log-helpers'
import { colors } from './tokens'
import styles from './ActivityLogDrawer.module.scss'

// ── design constants (Table-Option1.dc.html · "activity log side panel") ────
const DRAWER_WIDTH = 440
const DRAWER_BORDER = '#E1E5E9'
const DRAWER_SHADOW = '-14px 0 30px -18px rgba(0,0,0,0.28)'

// ── icons (inline SVGs from the design) ─────────────────────────────────────

const CloseIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 18, height: 18 }}
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

const ClockIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 34, height: 34, color: '#C3CDD6' }}
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

// ── event → view-model mapping ───────────────────────────────────────────────

/** Event-type tone — a LOCAL closed set mapped to modifier classes below. */
type EventTone = 'blue' | 'danger' | 'warning' | 'muted'

const TONE_CLASS: Record<EventTone, string> = {
  blue: styles.toneBlue,
  danger: styles.toneDanger,
  warning: styles.toneWarning,
  muted: styles.toneMuted,
}

interface EventDisplay {
  label: string
  tone: EventTone
}

const eventDisplay = (eventType: string): EventDisplay => {
  switch (eventType) {
    case 'TRANSACTION_ALLOCATED':
      return { label: 'Allocated', tone: 'blue' }
    case 'TRANSACTION_DEALLOCATED':
      return { label: 'Removed', tone: 'danger' }
    case 'REFUND_CLASSIFIED':
      return { label: 'Refund', tone: 'warning' }
    case 'ISSUE_RESOLVED':
      return { label: 'Resolved', tone: 'blue' }
    case 'EXPECTED_PRICE_SET':
      return { label: 'Expected set', tone: 'muted' }
    case 'EXPLICIT_CREDIT_RECORDED':
      return { label: 'Credited', tone: 'warning' }
    case 'EXPLICIT_CREDIT_DELETED':
      return { label: 'Credit removed', tone: 'danger' }
    case 'NOTE_ADDED':
      return { label: 'Note', tone: 'muted' }
    default:
      return { label: 'Update', tone: 'muted' }
  }
}

const payloadNote = (e: HistoryEvent): string | null => {
  const note = e.payload?.note
  return typeof note === 'string' && note.trim() ? note.trim() : null
}

const describeEvent = (e: HistoryEvent): string => {
  const note = payloadNote(e)
  const month = e.periodStartDate
    ? dayjs(e.periodStartDate).format('MMM YYYY')
    : null
  switch (e.eventType) {
    case 'TRANSACTION_ALLOCATED':
      return (
        note ?? (month ? `Payment allocated to ${month}` : 'Payment allocated')
      )
    case 'TRANSACTION_DEALLOCATED':
      return (
        note ?? (month ? `Payment removed from ${month}` : 'Payment removed')
      )
    case 'ISSUE_RESOLVED':
      return note ? `Discrepancy resolved — ${note}` : 'Discrepancy resolved'
    case 'EXPECTED_PRICE_SET':
      return note ?? 'Expected amount updated'
    case 'EXPLICIT_CREDIT_RECORDED':
      return note ?? 'Explicit credit recorded'
    case 'EXPLICIT_CREDIT_DELETED':
      return note ?? 'Explicit credit removed'
    case 'NOTE_ADDED':
      return note ?? 'Note added'
    default: {
      const humanized = e.eventType.toLowerCase().replace(/_/g, ' ')
      return note ?? humanized.charAt(0).toUpperCase() + humanized.slice(1)
    }
  }
}

interface ActivityEntry {
  id: string
  typeLabel: string
  typeTone: EventTone
  monthLabel: string
  /** Raw periodStartDate (YYYY-MM-DD) — month-filter value; null = General. */
  monthValue: string | null
  when: string
  desc: string
  byLabel: string
  amountFmt: string | null
  /** Short source transaction id, when the event references one. */
  txnRef: string | null
  /** Carrier · company context line, when resolved. */
  context: string | null
}

const shortId = (id: string): string =>
  id.length > 13 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id

const toEntry = (e: HistoryEvent): ActivityEntry => {
  const { label, tone } = eventDisplay(e.eventType)
  const context = [e.carrierName, e.companyName].filter(Boolean).join(' · ')
  return {
    id: e.id,
    typeLabel: label,
    typeTone: tone,
    monthLabel: e.periodStartDate
      ? dayjs(e.periodStartDate).format('MMMM YYYY')
      : 'General',
    monthValue: e.periodStartDate,
    when: dayjs(e.createdAt).format('MMM D, YYYY · h:mm A'),
    desc: describeEvent(e),
    byLabel: e.createdBy || 'System',
    amountFmt: formatAmount(e.amount),
    txnRef: e.transactionId ? shortId(e.transactionId) : null,
    context: context.length > 0 ? context : null,
  }
}

// ── component ────────────────────────────────────────────────────────────────

export interface ActivityLogDrawerProps {
  open: boolean
  onClose: () => void
  benefitId: string | null
  /** Insured/employee label for the subheader. */
  benefitLabel?: string
  /** Pre-select the month filter (YYYY-MM-DD) — cell history opens on its month. */
  initialMonth?: string
}

/**
 * Right-side "Activity log" drawer: a benefit's reconciliation activity
 * history (reallocations, flags, notes) with a client-side month filter.
 */
const ActivityLogDrawer: React.FC<ActivityLogDrawerProps> = ({
  open,
  onClose,
  benefitId,
  benefitLabel,
  initialMonth,
}) => {
  const [monthFilter, setMonthFilter] = useState<string>(initialMonth ?? 'all')

  // Fresh filter each time the drawer opens (or targets a new benefit/month):
  // seed to the cell's month when opened from a cell, else "all". Reset during
  // render (not in an effect) so the first post-change render already shows the
  // correct slice — an effect would leave one frame of the previous value.
  const filterKey = `${String(open)}|${benefitId ?? ''}|${initialMonth ?? ''}`
  const [prevFilterKey, setPrevFilterKey] = useState<string>(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setMonthFilter(initialMonth ?? 'all')
  }

  const { data, isLoading } = useBenefitActivity(benefitId, {
    enabled: open && !!benefitId,
  })

  // TODO(EMS-9035): consider BE-side filter/sort (deferred — API change).
  const events = useMemo(() => {
    if (!data?.data || !benefitId) return []
    // Belt-and-suspenders: `search` can match more than benefitId.
    return data.data
      .filter((e) => e.benefitId === benefitId)
      .slice()
      .sort(
        (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      )
  }, [data, benefitId])

  const entries = useMemo(() => events.map(toEntry), [events])

  const monthOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const e of entries) {
      if (e.monthValue && !seen.has(e.monthValue)) {
        seen.set(e.monthValue, e.monthLabel)
      }
    }
    // Always include the pre-selected month, even when it has no events — else
    // the <select> can't display it (it'd fall back to "All months" while the
    // state filters to an empty month), which is exactly the "not working" case.
    if (initialMonth && !seen.has(initialMonth)) {
      seen.set(initialMonth, dayjs(initialMonth).format('MMMM YYYY'))
    }
    const months = Array.from(seen.entries())
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([value, label]) => ({ value, label }))
    return [{ value: 'all', label: 'All months' }, ...months]
  }, [entries, initialMonth])

  const visible = useMemo(
    () =>
      monthFilter === 'all'
        ? entries
        : entries.filter((e) => e.monthValue === monthFilter),
    [entries, monthFilter]
  )

  const title = (
    <div className={styles.headerRow}>
      <div>
        <div className={styles.headerTitle}>Activity log</div>
        {benefitLabel && <div className={styles.headerSub}>{benefitLabel}</div>}
      </div>
      <button
        type="button"
        aria-label="Close"
        className={styles.closeBtn}
        onClick={onClose}
      >
        <CloseIcon />
      </button>
    </div>
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={DRAWER_WIDTH}
      title={title}
      closable={false}
      styles={{
        wrapper: { boxShadow: DRAWER_SHADOW },
        content: { borderLeft: `1px solid ${DRAWER_BORDER}` },
        header: {
          padding: '22px 24px 18px',
          borderBottom: `1px solid ${colors.border.subtle}`,
        },
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
      }}
    >
      <div className={styles.filterRow}>
        <select
          className={styles.monthSelect}
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          {monthOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.list}>
        {isLoading && (
          <span className={styles.emptyText}>Loading activity…</span>
        )}

        {!isLoading && visible.length === 0 && (
          <div className={styles.emptyWrap}>
            <ClockIcon />
            <span className={styles.emptyText}>
              No activity matches these filters.
            </span>
          </div>
        )}

        {visible.map((e) => (
          <div className={styles.entryCard} key={e.id}>
            <div className={styles.entryTopRow}>
              <span className={`${styles.overline} ${TONE_CLASS[e.typeTone]}`}>
                {e.typeLabel} · {e.monthLabel}
              </span>
              <span className={styles.when}>{e.when}</span>
            </div>
            <div className={styles.desc}>{e.desc}</div>
            {e.context ? (
              <div className={styles.contextLine}>{e.context}</div>
            ) : null}
            <div className={styles.entryBottomRow}>
              <span className={styles.byLabel}>
                {e.byLabel}
                {e.txnRef ? (
                  <span className={styles.txnRef}>TXN {e.txnRef}</span>
                ) : null}
              </span>
              {e.amountFmt && (
                <span className={styles.amount}>{e.amountFmt}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  )
}

export default ActivityLogDrawer
